import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import FieldWrapper from '../../../components/formInputs/FieldWrapper';
import SectionHeader from '../../../components/SectionHeader';
import Button from '../../../components/buttons/Button';
import { useRef } from 'react';
import FormUpload from '../../../components/formInputs/FormUpload';
import { getMedia, scrollToError } from '../../../utils/HelperFunctions';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IAccessory } from '../types/interfaces';
import SwitchActivation from '../../../components/layout/SwitchActivation';
import useFormsIntegrationHelpers from '../../../hooks/useFormsIntegrationHelpers';
import { customStyles } from '../../../utils/SelectStyles';
import FormDynamicSkeleton from '../../../components/loaders/FormDynamicSkeleton';

const CreateAccessoryForm = () => {
  const { id } = useParams();
  const formRef = useRef(null);
  const { t } = useTranslation();

  const { apiData, getDataLoading, handleSubmit, submitLoading } = useFormsIntegrationHelpers({
    queryKey: ['accessories', id],
    invalidateQueryKey: ["accessories"],
    id,
    singleGetApi: `/admin/accessories/${id}`,
    addApi: '/admin/accessories',
    editApi: `/admin/accessories/${id}`,
    listRoute: '/accessories',
    itemName: t('accessory')
  });

  const validationSchema = Yup.object({
    code: Yup.string().required(t('required')),
    name: Yup.object().shape({
      ar: Yup.string()
        .required(t('required'))
        // .matches(/^[\u0600-\u06FF\0-9s]+$/, t('must_ar'))
        .min(3, t('name_short')),
      en: Yup.string()
        .required(t('required'))
        // .matches(/^[A-Za-z0-9\s]+$/, t('must_en'))
        .min(3, t('name_short'))
    })
  });

  const initialValues: IAccessory = {
    ...apiData,
    
    name: {
      en: apiData?.name?.en || '',
      ar: apiData?.name?.ar || ''
    },
    description: {
      en: apiData?.description?.en || '',
      ar: apiData?.description?.ar || ''
    },
    
    hidden: apiData?.hidden !== undefined ? (apiData.hidden ? 1 : 0) : 0,
    new_or_used: apiData?.new_or_used || '',
    
    image: getMedia(apiData?.media, 'image')?.url || ''
  };

  const newUsedOptions = [
    { label: 'New', value: 'new' },
    { label: 'Used', value: 'used' }
  ];

  if (getDataLoading)
    return (
      <FormDynamicSkeleton
        sections={[
          [{ columns: 2, items: 1, type: 'textarea' }],
          [
            { columns: 2, items: 1 },
            { columns: 2, items: 2 },
            { columns: 2, items: 2 },
            { columns: 2, items: 2, type: 'textarea' },
            { columns: 2, items: 2 },
            { columns: 2, items: 1, type: 'switch' },
          ]
        ]}
        buttons={1}
      />
    );

  return (
    <div className={'form_section'} ref={formRef}>
      <Formik
        validateOnMount
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, { setErrors }) => {
          let value = { ...values, image: '', _method: 'POST' };
          if (id) {
            value = { ...value, _method: 'PUT' };
          }
          if ((values?.image as any) instanceof File) {
            value.image = values?.image;
          }
          handleSubmit({ ...value }, setErrors);
        }}
      >
        {(formik) => (
          <>
            <Form>
              <SectionHeader title={t('image')}>
                <div className="inputs_group">
                  <FieldWrapper>
                    <FormUpload errors={formik?.errors.image as string} touched={formik?.touched?.image as boolean} formik={formik} name={`image`} label={t('image')} />
                  </FieldWrapper>
                  <FieldWrapper noPadding />
                </div>
              </SectionHeader>
              <SectionHeader title={t('accessory_details')} customStyle="last">
                <div className="inputs_group">
                  <FieldWrapper title={t('code')} inputName={`code`} inputPlaceholder={t('code')} input customPadding />
                  <FieldWrapper />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('name_en')} inputName={`name[en]`} inputPlaceholder={t('name_en')} input customPadding />
                  <FieldWrapper title={t('name_ar')} inputName={`name[ar]`} inputPlaceholder={t('name_ar')} input customPadding />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('display_name_en')} inputName={`display_name[en]`} inputPlaceholder={t('display_name_en')} input customPadding />
                  <FieldWrapper title={t('display_name_ar')} inputName={`display_name[ar]`} inputPlaceholder={t('display_name_ar')} input customPadding />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('description_en')} inputName={`description[en]`} inputPlaceholder={t('description_en')} textArea customPadding maxLength={2200}/>
                  <FieldWrapper title={t('description_ar')} inputName={`description[ar]`} inputPlaceholder={t('description_ar')} textArea customPadding maxLength={2200} />
                </div>
                <div className="inputs_group">
                  <FieldWrapper title={t('price')} inputName={`price`} inputPlaceholder={t('price')} input customPadding type="number" />
                  <FieldWrapper
                    customPadding
                    title={t('new_or_used')}
                    inputPlaceholder={t(`new_or_used`)}
                    inputName={`new_or_used`}
                    options={newUsedOptions}
                    selectStyle={customStyles}
                    defaultValue={newUsedOptions?.find((section) => section.value === formik.values.new_or_used)}
                    select
                    formik={formik}
                  />
                </div>
                <div className="inputs_group">
                  <FieldWrapper>
                    <div className="switch_container">
                      <SwitchActivation onChange={(value: any) => formik.setFieldValue('hidden', value?.target?.checked ? 1 : 0)} active={formik.values.hidden} />
                      <p>
                        {t('hide_from_website')} <span className="side_note">({t('active_means_hide')})</span>
                      </p>
                    </div>
                  </FieldWrapper>
                </div>
              </SectionHeader>
              <div className="form_button reverse">
                <Button
                  loading={submitLoading}
                  onClick={() => {
                    scrollToError(!formik.isValid, formRef);
                  }}
                >
                  <span className="bold">{t('save')}</span>
                </Button>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
};

export default CreateAccessoryForm;
